import { useState, type ReactElement } from "react";
import { createTenantPeopleSource } from "@playbookai/api-clients";
import {
  Alert,
  Button,
  FormField,
  Input,
  Modal,
  PeopleField,
  Select,
  Textarea,
  toast,
  type PeopleFieldValue,
} from "@playbookai/ui";
import { searchTenantPeople } from "../../api/tenant-people-source.js";
import type { Team, TeamMember } from "../../api/teams-api.js";
import { useInviteToTeam, useUpdateTeam } from "./useTeams.js";

/**
 * The team page's form dialogs. Each owns the mutation it submits, so its
 * failure renders inside the dialog, next to the fields it is about, and an
 * active form error survives anything happening on the page behind it.
 *
 * ONE ACCENT EACH (DS invariant §9): the confirm is `primary`, or
 * `destructive` where it retires something, and Cancel is `secondary`.
 */

/* The invitee field's source: the tenant directory, grant mode `none` (a team
   is not an access list) and no "Assign to me" row — nobody invites
   themselves. Module-level, so the field's search cache survives re-renders. */
const inviteeSource = createTenantPeopleSource({ search: searchTenantPeople });

/**
 * Invite somebody to the team.
 *
 * AN INVITATION, NOT AN ADDITION. The person is not on the team until they
 * accept, and the copy says so where the action is. It lived as an always-open
 * card on the team page; it is a dialog now, opened from the header's one
 * primary action, so the roster is the first thing an owner sees.
 */
export function InviteTeamDialog({
  open,
  team,
  onClose,
}: {
  open: boolean;
  team: Team;
  onClose: () => void;
}): ReactElement {
  const invite = useInviteToTeam(team.teamId);
  /* Seeded once per opening by the caller's `key`, like the edit dialog: a
     fresh open is a fresh form and a fresh mutation state. */
  const [invitee, setInvitee] = useState<PeopleFieldValue>(null);

  const send = (): void => {
    if (invitee?.kind !== "person") return;
    invite.mutate(invitee.id, {
      onSuccess: () => {
        toast(`Invitation sent to ${invitee.name}.`, { tone: "healthy" });
        onClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Invite people to ${team.name}`}
      description="They join once they accept. Being on a team changes nobody's access."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!invitee || invite.isPending}
            onClick={send}
          >
            {invite.isPending ? "Sending…" : "Send invitation"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label="Person">
          <PeopleField
            label="Person"
            emptyLabel="Choose a person"
            value={invitee}
            onChange={setInvitee}
            source={inviteeSource}
          />
        </FormField>
        {invite.isError && (
          <Alert
            role="status"
            tone="critical"
            title="The invitation was not sent"
          >
            {invite.error instanceof Error
              ? invite.error.message
              : "Try again."}
          </Alert>
        )}
      </div>
    </Modal>
  );
}

/** Mirrors `teams_name_check` / `teams_description_check`. A client that
 *  accepted more would only discover the refusal at the write. */
const NAME_MAX = 120;
const DESCRIPTION_MAX = 500;

/**
 * Edit the team's name and description — the owner's own details (ADR-0082).
 *
 * IT CARRIES `team.version`, like every other owner mutation, and a stale one
 * is refused rather than merged. The dialog keeps what was typed when that
 * happens: the reader's words are theirs, and discarding them on a conflict
 * would punish the person who lost a race they could not see. Reload re-reads
 * the team — the caller's `onReload` — and the dialog re-seeds from the fresh
 * row when it is next opened.
 */
export function EditTeamDialog({
  open,
  team,
  onClose,
  onSaved,
  onReload,
}: {
  open: boolean;
  team: Team;
  onClose: () => void;
  /** Tells the page an older failure notice is now obsolete — a successful
   *  save is exactly the kind of thing that makes one stale. */
  onSaved: () => void;
  onReload: () => void;
}): ReactElement {
  const update = useUpdateTeam(team.teamId);
  /* SEEDED ONCE PER OPENING, by the `key` the caller puts on this component —
     not by an effect that re-syncs. A background refetch landing mid-edit must
     not overwrite what somebody is typing, and an effect guarded on `open`
     would have to lie to the dependency linter to say so. Remounting is the
     honest expression: a fresh open is a fresh form, and it resets the
     mutation's error with it. */
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description ?? "");

  const trimmed = name.trim();
  const unchanged =
    trimmed === team.name &&
    (description.trim() || null) === (team.description ?? null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit team details"
      description="The name and the description. Membership and ownership are changed on the Members tab."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!trimmed || unchanged || update.isPending}
            onClick={() =>
              update.mutate(
                {
                  name: trimmed,
                  description: description.trim() || null,
                  version: team.version,
                },
                {
                  onSuccess: () => {
                    onSaved();
                    onClose();
                  },
                },
              )
            }
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          maxLength={NAME_MAX}
          onChange={(event) => setName(event.target.value)}
        />
        <Textarea
          label="Description (optional)"
          value={description}
          maxLength={DESCRIPTION_MAX}
          onChange={(event) => setDescription(event.target.value)}
          hint="Team names are not unique — a line about what this team is for helps people tell yours apart."
        />
        {update.isError && (
          <Alert
            role="status"
            tone="critical"
            title="These details were not saved"
          >
            <div className="space-y-2">
              <p>
                {update.error instanceof Error
                  ? update.error.message
                  : "Try again."}
              </p>
              {/* What you typed is still in the fields above — reloading the
                  team does not discard it. */}
              <Button variant="secondary" size="sm" onClick={onReload}>
                Reload this team
              </Button>
            </div>
          </Alert>
        )}
      </div>
    </Modal>
  );
}

export type AdminAction = "recover" | "archive" | null;

/** Mirrors the server's `MIN_REASON_LENGTH` / `MAX_REASON_LENGTH` on both admin
 *  routes. A client that accepted less would only learn at the write. */
const REASON_MIN = 8;
const REASON_MAX = 500;

/**
 * One dialog for both administrator acts, because they ask the same two
 * questions — who, and why — and the reason is the half that matters.
 *
 * THE REASON IS REQUIRED AND AUDITED. Both server routes refuse a reason under
 * eight characters, and both record it on their own audit action
 * (`team_ownership_recovered`, `team_archived_by_admin`) so an out-of-band act
 * is never indistinguishable from an owner's own.
 *
 * THE CANDIDATE LIST IS THE ROSTER, NARROWED THE WAY THE SERVER NARROWS IT:
 * accepted members who are still deliverable, which is exactly
 * `requireActiveTargetMember`. Somebody who has left the workspace is on the
 * roster and visibly unreachable, and promoting them would be refused — so they
 * are not offered. Existing owners are not offered either: promoting an owner
 * is a no-op the server would accept and nobody needs.
 *
 * Recovery is `primary`; archiving retires the team, so it is `destructive`.
 */
export function AdminActionDialog({
  action,
  team,
  members,
  membersHasMore,
  onClose,
  onRecover,
  onArchive,
  pending,
}: {
  action: AdminAction;
  team: Team;
  members: readonly TeamMember[];
  membersHasMore: boolean;
  onClose: () => void;
  onRecover: (input: { userId: string; reason: string }) => void;
  onArchive: (input: { reason: string }) => void;
  pending: boolean;
}): ReactElement {
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");

  const candidates = members.filter(
    (row) => row.deliverable && row.role !== "owner",
  );
  const recovering = action === "recover";
  const trimmedReason = reason.trim();
  const ready =
    trimmedReason.length >= REASON_MIN && (!recovering || userId !== "");

  const close = (): void => {
    setUserId("");
    setReason("");
    onClose();
  };

  return (
    <Modal
      open={action !== null}
      onClose={close}
      title={
        recovering
          ? `Recover ownership of ${team.name}`
          : `Archive ${team.name}?`
      }
      description={
        recovering
          ? "Promotes somebody already on this team to owner. It cannot add anybody, and it grants no access beyond this team's roster."
          : "Only for a team with no owner left who can act. Its roster stays readable, outstanding requests are cancelled, and there is no way to un-archive it."
      }
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            variant={recovering ? "primary" : "destructive"}
            disabled={!ready || pending}
            onClick={() =>
              recovering
                ? onRecover({ userId, reason: trimmedReason })
                : onArchive({ reason: trimmedReason })
            }
          >
            {recovering ? "Recover ownership" : "Archive"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {recovering &&
          (candidates.length === 0 ? (
            <Alert
              role="status"
              tone="info"
              title="Nobody on this team can be promoted"
            >
              Ownership can only be recovered to somebody who is already an
              accepted member and can still be reached. If everybody has left
              the workspace, archiving the team is the only way out.
            </Alert>
          ) : (
            <Select
              label="New owner"
              value={userId}
              placeholder="Choose a member"
              onChange={setUserId}
              options={candidates.map((row) => ({
                value: row.userId,
                label: row.displayName ?? row.primaryEmail ?? row.userId,
              }))}
              {...(membersHasMore
                ? {
                    hint: "Only the members loaded so far are listed — load more of the roster to choose somebody further down it.",
                  }
                : {})}
            />
          ))}
        <Textarea
          label="Reason"
          value={reason}
          maxLength={REASON_MAX}
          onChange={(event) => setReason(event.target.value)}
          hint={`Recorded in the audit log. At least ${REASON_MIN} characters — an out-of-band change has to explain itself afterwards.`}
        />
      </div>
    </Modal>
  );
}
